import { useIntl } from 'react-intl'
import { usePipelineStore } from '../../../stores/PipelineStore'
import useUser from '../../../hooks/User'
import { ListProduct } from '../../../interfaces'
import { changeValueIfStringIntoNumber, currencyFormatter, currencyFormatterPreventPropagation, customNumberFormat, customThousandSeparatorDot, getDiscountType, roundDecimalToTwo } from '../../../functions/general'
import { checkIsCompanyEsb } from '../../../functions/customCase'
import { useCustomeStore } from '../../../stores/CustomeStore'
import { useProductStore } from '../../../stores/ProductStore'
import { useSettingsStore } from '../../../stores/SettingsStore'
import LabelTooltip from '../../General/LabelTooltip'
import { TempPaymentTermin } from '../../../interfaces/Termin/Termin'

interface ListProductPipelineProps {
  listProducts: ListProduct[]
  setListProducts: (products: ListProduct[]) => void
  setShowModalAddProduct: (value: boolean) => void
  handleAlertAddProduct?: (desc: string) => void
  tempPaymentTermin?: TempPaymentTermin[]
}

const ListProductPipeline = ({ listProducts, setListProducts, setShowModalAddProduct, handleAlertAddProduct, tempPaymentTermin }: ListProductPipelineProps) => {
  const intl = useIntl()
  const user = useUser()
  const { selectMultiPipeline } = usePipelineStore()
  const { settings } = useSettingsStore()
  const { companyId } = useCustomeStore()
  const { setProductTemp, setTypeActionProduct } = useProductStore()

  function handleDeleteProduct(index: number) {
    if (tempPaymentTermin?.some((item: any) => item.inputType === 'percentage')) {
      handleAlertAddProduct?.(intl.formatMessage({ id: 'FORM.LABEL.DELETE' }).toLocaleLowerCase())
    }
    else {
      setListProducts(listProducts.filter((_, i) => i !== index))
    }
  }

  function handleUpdateProduct(index: number) {
    if (tempPaymentTermin?.some((item: any) => item.inputType === 'percentage')) {
      handleAlertAddProduct?.(intl.formatMessage({ id: 'FORM.LABEL.UPDATE' }).toLocaleLowerCase())
    }
    else {
      listProducts[index].edit = true
      setShowModalAddProduct(true)
      setProductTemp({ ...listProducts[index], index: index })
      setListProducts([...listProducts])
      setTypeActionProduct('edit')
    }
  }

  return (
    <div className="table-responsive mt-3">
      <table className="table table-striped" style={{ overflowX: "auto" }}>
        <thead>
          <tr className="fw-bold fs-6 text-gray-800 border-bottom border-gray-200">
            <th>{intl.formatMessage({ id: "FORM.MENU.PRODUCTS" })}</th>
            <th className="text-end">{intl.formatMessage({ id: "FORM.LABEL.UNITS" })}</th>
            {
              user.data.is_allowed_access_hpp && selectMultiPipeline.isFeatureCogsEnabled &&
              <th className="text-end">{intl.formatMessage({ id: "FORM.LABEL.HPP" })}</th>
            }
            <th className="text-end">{intl.formatMessage({ id: "FORM.LABEL.NOTE" })}</th>
            <th className="text-end">{intl.formatMessage({ id: "FORM.LABEL.QUANTITY" })}</th>
            <th className="text-end">{checkIsCompanyEsb(companyId, user) ? "Projected revenue" : intl.formatMessage({ id: "FORM.LABEL.PRICE" }) + ` (${selectMultiPipeline.currencyCode || 'IDR'})`}</th>
            {
              !settings.is_pipeline_discount_hidden && (
                <th className="text-end">{intl.formatMessage({ id: "FORM.LABEL.DISCOUNT" })}</th>
              )
            }
            <th className="text-end">{intl.formatMessage({ id: "FORM.LABEL.VAT" })}</th>
            <th className="text-end">{intl.formatMessage({ id: "FORM.LABEL.TOTAL" }, { title: ` (${selectMultiPipeline.currencyCode || 'IDR'})` })}</th>
          </tr>
        </thead>
        {
          listProducts.length > 0 ?
            <tbody>
              {
                listProducts.map((product, index) => {
                  const totalProduct = (product.leadProductTotal as string).replaceAll(",", "")
                  const currencyFormatterValidation = currencyFormatterPreventPropagation(totalProduct);
                  let getTotalProductVat = '0'
                  if (currencyFormatterValidation.hasTrailingDot || currencyFormatterValidation.hasTrailingDotZero) {
                    getTotalProductVat = totalProduct
                  } else {
                    getTotalProductVat = (
                      selectMultiPipeline.currencyCode === 'IDR' && selectMultiPipeline.currencyIsRounded
                        ?
                        Math.round(Number(totalProduct)).toString()
                        :
                        roundDecimalToTwo(Number(totalProduct)).toString() //+ (Number(product.leadProductVatNominal ?? 0))
                    )
                  }
                  var labels = product.leadProductLabels?.map((label: any) => ({ text: label.label.name, color: label.label.color })) || [];

                  return (
                    <tr key={index} style={{ verticalAlign: "middle" }}>
                      <td style={{ minWidth: "200px" }}>
                        <div className="d-flex align-items-center">
                          {product.productSku &&
                            product.productName.indexOf(" (SKU:") !== -1 ?
                            product.productName.substring(0, product.productName.indexOf(" (SKU:")) :
                            product.productName
                          }
                          {
                            labels.length > 0 &&
                            <LabelTooltip data={labels}>
                              {labels}
                            </LabelTooltip>
                          }
                        </div>
                        {product.productSku &&
                          <>
                            <br />
                            <small className="text-muted fst-italic">SKU: {product.productSku}</small>
                          </>
                        }
                      </td>
                      <td className="text-end ps-0" style={{ minWidth: "100px" }}>
                        {
                          product.unitName ? product.unitName : "PCS"
                        }
                      </td>
                      {
                        user.data.is_allowed_access_hpp && selectMultiPipeline.isFeatureCogsEnabled &&
                        // HPP
                        <td className="text-end ps-0" style={{ minWidth: "100px" }}>
                          {
                            (product.leadProductHpp || product.leadProductHpp == '0') ? currencyFormatter(product.leadProductHpp, selectMultiPipeline.currencyIsRounded) : ""
                          }
                        </td>
                      }
                      {/* NOTE */}
                      <td className="text-end ps-0" style={{ minWidth: "70px" }}>
                        {
                          product.leadProductNotes ?? '-'
                        }
                      </td>

                      {/* QTY */}
                      <td className="text-end ps-0" style={{ minWidth: "70px" }}>
                        {
                          customThousandSeparatorDot(product.leadProductQty)
                        }
                      </td>
                      {/* PRICE */}
                      <td className="text-end ps-0" style={{ minWidth: "150px" }}>
                        {
                          <span className="d-flex flex-column justify-content-end">
                            {
                              product.leadProductOriginalPrice ?
                                currencyFormatter(
                                  typeof product.leadProductOriginalPrice === 'string'
                                    ? changeValueIfStringIntoNumber(product.leadProductOriginalPrice)
                                    : product.leadProductOriginalPrice,
                                  selectMultiPipeline.currencyIsRounded
                                )
                                : (product.leadProductPrice ? currencyFormatter(changeValueIfStringIntoNumber(product.leadProductPrice), selectMultiPipeline.currencyIsRounded) : "0")
                            }
                            {
                              product.leadProductIsPriceIncludeVat &&
                              <span className="text-muted fst-italic ms-2">
                                {intl.formatMessage({ id: "FORM.LABEL.INCLUDE_VAT" })}
                              </span>
                            }
                          </span>
                        }
                      </td>
                      {/* DISCOUNT */}
                      {
                        !settings.is_pipeline_discount_hidden && (
                          <td className="text-end ps-0" style={{ minWidth: "100px" }}>
                            {
                              <div className="d-flex justify-content-end gap-1">
                                <span>
                                  {
                                    product.leadProductPrice ? currencyFormatter(product.leadProductDiscount || "", selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode, { showPrefix: product.leadProductDiscountType === "PERCENTAGE" ? false : true }) : "0"
                                  }
                                </span>
                                {
                                  product.leadProductDiscountType === "PERCENTAGE" &&
                                  <span>
                                    {` (${getDiscountType(product.leadProductDiscountType, selectMultiPipeline)})`}
                                  </span>
                                }
                              </div>
                            }
                          </td>
                        )
                      }
                      {/* VAT */}
                      <td className="text-end ps-0" style={{ minWidth: "100px" }}>
                        {
                          product.leadProductVatPercentage !== null ? customNumberFormat(Number(product.leadProductVatPercentage)) : '-'
                        }
                      </td>
                      {/* TOTAL */}
                      <td className="text-end ps-0" style={{ minWidth: "100px" }}>
                        {
                          currencyFormatter(getTotalProductVat, selectMultiPipeline.currencyIsRounded)
                        }
                      </td>
                      {/* ACTION */}
                      <td className="text-end ps-0" style={{ minWidth: "100px" }}>
                        {
                          <div className="d-flex ms-2">
                            <button type="button" className="btn btn-sm btn-icon btn-light-primary me-3" onClick={() => handleUpdateProduct(index)}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => handleDeleteProduct(index)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        }
                      </td>
                    </tr>
                  )
                })
              }
            </tbody>
            :
            <tbody>
              <tr>
                <td colSpan={settings.is_pipeline_discount_hidden ? 8 : 9} className="text-center">
                  {intl.formatMessage({ id: "FORM.VALIDATION.NO_CHOSEN" }, { title: intl.formatMessage({ id: "FORM.MENU.PRODUCTS" }).toLowerCase() })}
                </td>
              </tr>
            </tbody>
        }
      </table>
    </div>
  )
}

export default ListProductPipeline